import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, ArrowLeft, Loader2, Clock, MessageSquare, ShieldCheck } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge, CategoryBadge } from "@/components/Badges";
import api, { formatApiErrorDetail } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function Track() {
  const [caseNumber, setCaseNumber] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setData(null);
    try {
      const res = await api.post("/concerns/track", { case_number: caseNumber, access_code: accessCode });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't find that concern.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-forest-800 mb-6">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to home
        </Link>
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">Check your status</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">Track a concern</h1>
        <p className="mt-4 text-charcoal-600">Enter the case number and access code you received when you submitted.</p>

        <form onSubmit={lookup} className="mt-8 bg-white rounded-2xl border border-forest-600/10 shadow-sm p-6 space-y-5">
          <div>
            <Label htmlFor="cn" className="text-forest-900 font-semibold">Case number</Label>
            <Input id="cn" data-testid="track-case-number-input" value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)}
              placeholder="ER-2026-0001" className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
          </div>
          <div>
            <Label htmlFor="ac" className="text-forest-900 font-semibold">Access code</Label>
            <Input id="ac" data-testid="track-access-code-input" value={accessCode} onChange={(e) => setAccessCode(e.target.value)}
              placeholder="6-character code" className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
          </div>
          <Button type="submit" disabled={loading} data-testid="track-submit-btn"
            className="w-full rounded-full bg-forest-600 hover:bg-forest-700 text-bone py-6 text-base">
            {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Looking up…</> : <><Search className="h-5 w-5 mr-2" strokeWidth={1.5} /> Find my concern</>}
          </Button>
        </form>

        {data && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white rounded-2xl border border-forest-600/10 shadow-sm p-6" data-testid="track-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-charcoal-600">{data.case_number}</p>
                <h2 className="font-serif text-2xl text-forest-900 mt-1">{data.title}</h2>
              </div>
              <StatusBadge status={data.status} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-charcoal-600">
              <CategoryBadge category={data.category} />
              {data.campus && <span>• {data.campus}</span>}
              {data.assigned_to_name && <span>• Specialist: {data.assigned_to_name}</span>}
            </div>

            <div className="mt-6 rounded-xl bg-bone-100/60 p-4 flex items-center gap-2 text-sm text-forest-800">
              <ShieldCheck className="h-4 w-4 text-forest-600" strokeWidth={1.5} /> Submitted {fmt(data.created_at)} • Last updated {fmt(data.updated_at)}
            </div>

            <h3 className="mt-7 font-serif text-lg text-forest-900">Activity</h3>
            <ol className="mt-4 space-y-4">
              {data.timeline.map((ev) => (
                <li key={ev.id} className="flex gap-3">
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-forest-50 text-forest-600 shrink-0">
                    {ev.type === "note" ? <MessageSquare className="h-4 w-4" strokeWidth={1.5} /> : <Clock className="h-4 w-4" strokeWidth={1.5} />}
                  </span>
                  <div className="pb-1">
                    <p className="text-sm text-charcoal-700">{ev.content}</p>
                    <p className="text-xs text-charcoal-600 mt-0.5">{ev.author_name} • {fmt(ev.created_at)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
